/**
 * Sound Notifications Hook
 * Provides subtle audio cues for critical events using Web Audio API
 * 
 * Features:
 * - No external audio files needed (uses synthesized tones)
 * - User preference control (enable/disable)
 * - Different sounds for different event types
 * - Volume control
 * - Respects browser autoplay policies
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// Sound types and their configurations
const SOUND_CONFIGS = {
  // New message - soft, pleasant chime
  message: {
    frequencies: [523.25, 659.25], // C5, E5 - major third
    duration: 0.15,
    type: 'sine',
    volume: 0.3,
  },
  // High priority message - slightly more attention-grabbing
  messagePriority: {
    frequencies: [659.25, 783.99, 659.25], // E5, G5, E5
    duration: 0.12,
    type: 'sine',
    volume: 0.4,
  },
  // User login - subtle rising tone
  userLogin: {
    frequencies: [392, 523.25], // G4, C5 - perfect fourth up
    duration: 0.1,
    type: 'sine',
    volume: 0.25,
  },
  // User logout - subtle falling tone
  userLogout: {
    frequencies: [523.25, 392], // C5, G4 - perfect fourth down
    duration: 0.1,
    type: 'sine',
    volume: 0.2,
  },
  // Success - pleasant confirmation
  success: {
    frequencies: [523.25, 659.25, 783.99], // C5, E5, G5 - major chord
    duration: 0.1,
    type: 'sine',
    volume: 0.3,
  },
  // Warning - attention needed
  warning: {
    frequencies: [440, 440], // A4 repeated
    duration: 0.15,
    type: 'triangle',
    volume: 0.35,
  },
  // Error - distinct alert
  error: {
    frequencies: [220, 196], // A3, G3 - minor second down
    duration: 0.2,
    type: 'square',
    volume: 0.25,
  },
  // Mention - someone mentioned you
  mention: {
    frequencies: [587.33, 783.99, 987.77], // D5, G5, B5
    duration: 0.1,
    type: 'sine',
    volume: 0.4,
  },
};

// Local storage key for preferences
const STORAGE_KEY = 'rhino_sound_preferences';

// Default preferences - sounds disabled by default
const DEFAULT_PREFERENCES = {
  enabled: false, // Disabled until notification system is fully implemented
  volume: 0.5, // 0-1 scale
  sounds: {
    message: false,
    messagePriority: false,
    userLogin: false,
    userLogout: false,
    success: false,
    warning: false,
    error: false,
    mention: false,
  },
};

export function useSoundNotifications() {
  const audioContextRef = useRef(null);
  const [preferences, setPreferences] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) } : DEFAULT_PREFERENCES;
    } catch {
      return DEFAULT_PREFERENCES;
    }
  });
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Initialize AudioContext (requires user interaction first due to browser policies)
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        setIsUnlocked(true);
      } catch (error) {
        console.warn('Web Audio API not supported:', error);
      }
    } else if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().then(() => setIsUnlocked(true));
    }
    return audioContextRef.current;
  }, []);

  // Unlock audio on first user interaction
  useEffect(() => {
    const unlockAudio = () => {
      initAudioContext();
      // Remove listeners after first interaction
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
  }, [initAudioContext]);

  // Save preferences to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save sound preferences:', error);
    }
  }, [preferences]);

  // Play a synthesized tone
  const playTone = useCallback((frequency, duration, type, volume) => {
    const ctx = audioContextRef.current;
    if (!ctx || ctx.state === 'suspended') return;

    try {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

      // Smooth envelope to avoid clicks
      const now = ctx.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + 0.01); // Quick attack
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration); // Smooth decay

      oscillator.start(now);
      oscillator.stop(now + duration + 0.05);
    } catch (error) {
      console.warn('Failed to play tone:', error);
    }
  }, []);

  // Play a sound by type
  const playSound = useCallback((soundType) => {
    // SOUNDS DISABLED - notification system not fully implemented yet
    // Remove this return statement when notification system is complete
    return;
    
    // Check if sounds are enabled globally
    if (!preferences.enabled) return;

    // Check if this specific sound type is enabled
    if (!preferences.sounds[soundType]) return;

    // Check if audio context is available
    if (!audioContextRef.current || audioContextRef.current.state === 'suspended') {
      initAudioContext();
      return;
    }

    const config = SOUND_CONFIGS[soundType];
    if (!config) {
      console.warn(`Unknown sound type: ${soundType}`);
      return;
    }

    const adjustedVolume = config.volume * preferences.volume;

    // Play each frequency in sequence
    config.frequencies.forEach((freq, index) => {
      setTimeout(() => {
        playTone(freq, config.duration, config.type, adjustedVolume);
      }, index * (config.duration * 1000 * 0.8)); // Slight overlap for smoother sound
    });
  }, [preferences, playTone, initAudioContext]);

  // Update preferences
  const updatePreferences = useCallback((updates) => {
    setPreferences(prev => ({
      ...prev,
      ...updates,
      sounds: {
        ...prev.sounds,
        ...(updates.sounds || {}),
      },
    }));
  }, []);

  // Toggle master sound
  const toggleSound = useCallback(() => {
    setPreferences(prev => ({ ...prev, enabled: !prev.enabled }));
  }, []);

  // Toggle specific sound type
  const toggleSoundType = useCallback((soundType) => {
    setPreferences(prev => ({
      ...prev,
      sounds: {
        ...prev.sounds,
        [soundType]: !prev.sounds[soundType],
      },
    }));
  }, []);

  // Set master volume
  const setVolume = useCallback((volume) => {
    setPreferences(prev => ({ ...prev, volume: Math.max(0, Math.min(1, volume)) }));
  }, []);

  // Test a sound
  const testSound = useCallback((soundType = 'message') => {
    // Temporarily enable to test
    const wasEnabled = preferences.enabled;
    const wasTypeEnabled = preferences.sounds[soundType];
    
    if (!wasEnabled || !wasTypeEnabled) {
      // Play without checking preferences for testing
      const ctx = initAudioContext();
      if (!ctx) return;
      
      const config = SOUND_CONFIGS[soundType];
      if (!config) return;
      
      const adjustedVolume = config.volume * preferences.volume;
      config.frequencies.forEach((freq, index) => {
        setTimeout(() => {
          playTone(freq, config.duration, config.type, adjustedVolume);
        }, index * (config.duration * 1000 * 0.8));
      });
    } else {
      playSound(soundType);
    }
  }, [preferences, playSound, playTone, initAudioContext]);

  return {
    // State
    preferences,
    isUnlocked,
    
    // Actions
    playSound,
    toggleSound,
    toggleSoundType,
    setVolume,
    updatePreferences,
    testSound,
    initAudioContext,
    
    // Sound types for reference
    soundTypes: Object.keys(SOUND_CONFIGS),
  };
}

export default useSoundNotifications;
